import { Component, ViewChild } from '@angular/core';
import { PaymentDialogComponent } from '@dato-dialogs/payment-dialog/payment-dialog.component';
import { DocumentEndpoint } from '@dato-proxy/endpoints/document.endpoint';
import { PdfEndpoint } from '@dato-proxy/endpoints/pdf.endpoint';
import { BaseDocument } from '@dato-proxy/entities/basedocument.entity';
import { BaseTransDocument } from '@dato-proxy/entities/basetransdocument.entity';
import { PaymentRequest } from '@dato-proxy/entities/paymentrequest.entity';
import { PdfView } from '@dato-proxy/views/pdf.view';
import { ToolbarSettings } from '@m4u-angular/base/classes/BaseClasses';
import { BasePageComponent } from '@m4u-angular/base/component/BasePageComponent';
import { ServiceBagService, ServiceBagServices } from '@m4u-angular/base/component/ServiceBag';
import { BaseEntity, BaseEntityResponse, BaseState } from '@m4u-angular/base/entities/BaseEntities';
import { EnumCRUDActions, EnumPageDisplayModes } from '@m4u-angular/core/framework/sysenums.enum';
import { SysGlobals } from '@m4u-angular/core/framework/sysglobals';
import { Utils } from '@m4u-angular/core/helpers/Utils';
import { PdfViewerComponent } from '@m4u-angular/ui/components/pdf-viewer/pdf-viewer.component';
import { Globals } from '../../_globals';
import { LoginRequestEndpoint } from './../../_proxy/endpoints/loginrequest.endpoint';

class State extends BaseState {
    PDFData: any;
    FileName: string;
    EntryID: number;
    TotalSum: number;
    IsPaymentAllowed: boolean;
    IsHasAdditionalDocuments: boolean;
    IsAllowLedger: boolean;
    IsAllowPayment: boolean;
    ViewType: EnumPdfDocumentViewType = EnumPdfDocumentViewType.External;
    DocumentType: number;
}

export enum EnumPdfDocumentViewType {
    Inner,
    External
}

@Component({
    providers: ServiceBagServices,
    selector: 'app-pdf-page',
    templateUrl: './pdf-page.component.html',
    styleUrls: ['./pdf-page.component.scss']
})
export class PdfPageComponent extends BasePageComponent<BaseEntity, State> {

    @ViewChild(PdfViewerComponent) pdfViewer: PdfViewerComponent;
    @ViewChild(PaymentDialogComponent) paymentDialog: PaymentDialogComponent;

    constructor(A: ServiceBagService,
        private pdfEndpoint: PdfEndpoint,
        private loginRequestEndpoint: LoginRequestEndpoint,
        private documentEndpoint: DocumentEndpoint) {
        super(A, 'PdfPageComponent', BaseEntity, State);
    }

    override InitState() {}

    override OnInitComponent() {
        this.State.IsAllowLedger = Globals.SystemOption.IsAllowLedger;
        this.State.IsAllowPayment = Globals.SystemOption.IsAllowPayment;
        this.State.EntryID = this.A.Navigator.GetRouteParams().EntryID;

        const queryParams = this.A.Navigator.GetQueryParams(true);
        queryParams?.ViewType && (this.State.ViewType = +queryParams?.ViewType);
        queryParams?.DocumentType && (this.State.DocumentType = +queryParams?.DocumentType);

        this.DisplayMode = this.State.ViewType === EnumPdfDocumentViewType.Inner 
            ? this.A.Screen.IsDesktop() ? EnumPageDisplayModes.Window : EnumPageDisplayModes.BottomSheet
            : EnumPageDisplayModes.FullScreen;

        this.LoadData();
    }

    override OnGetToolbarSettings() {
        const settings = {
            IsAllowPrint: true,
            OnPrintButtonClick: (e) => this.pdfViewer.instance.iframe.nativeElement.contentWindow.print()
        } as ToolbarSettings;
        return settings;
    }

    override OnExternalEntitySaved(ExternalEntity: BaseEntity, ExternalEntityType: new () => BaseEntity, ExternalEntityAction: EnumCRUDActions) {
        if (ExternalEntityType === PaymentRequest) { this.LoadData(); }
    }

    override GetTitle() {
        return this.State.FileName;
    }

    IsShowPayButton() {
        return this.State.IsAllowPayment && (this.State.IsPaymentAllowed == null || this.State.IsPaymentAllowed == true) && this.State.TotalSum > 0;
    }

    IsShowAdditionalDocumentsButton() {
        return this.State.IsHasAdditionalDocuments && this.State.IsAllowLedger;
    }

    GetLocale(): string {
        return SysGlobals.Language === 'HE' ? 'he' : 'en-US';
    }

    OnClickPay() {
        if (this.State.ViewType === EnumPdfDocumentViewType.Inner) {
            if (this.State.ViewType == null || this.State.DocumentType == null) {
                this.UI.StopMessage('Document type is wrong');
                return;
            }
            this.Do(this.documentEndpoint.GetDocument(this.State.EntryID, this.State.DocumentType), false, this.OnPayDocument);
        } else {
            this.Do(this.loginRequestEndpoint.GetRelatedDocument(this.State.EntryID), false, this.OnPayDocument);
        }
    }

    private OnPayDocument = (response: BaseEntityResponse<BaseDocument>) => {
        Globals.PaymentDocuments = [response.Entity];

        this.paymentDialog.ShowDialogShort(() => {});
    }

    OnSeeAdditional() {
        if (Globals.SystemOption.IsAllowLedger) { this.Navigate(['/pages/reports']);
        } else { this.Navigate(['/system/login']); }
    }

    IsNoToolbar() {
        return !Globals.IsAllowAccounting() && !Globals.SystemOption.IsAllowMyDetails;
    }

    private LoadData() {
        if (this.State.ViewType === EnumPdfDocumentViewType.Inner) {
            if (this.State.ViewType == null || this.State.DocumentType == null) {
                this.UI.StopMessage('Document type is wrong');
                return;
            }
            this.Do(this.documentEndpoint.GetDocumentForPdf(this.State.EntryID, this.State.DocumentType), false, (documentResponse) => {
                const transDocument = documentResponse.Entity as BaseTransDocument;
                if (!transDocument) {
                    this.UI.StopMessage('Document was not loaded correctly');
                    return;
                }
                this.Do(this.pdfEndpoint.GetPDFByLink(transDocument.PdfUrl), false, (response) => {
                    this.State.PDFData = Utils.ConvertToBlob(response.Data.toString(), 'application/pdf');
                    this.State.TotalSum = transDocument.PaymentSum;
                    this.State.FileName = `${transDocument.TransTypeString} - ${transDocument.DocumentNumber}`;
                    this.State.IsPaymentAllowed = transDocument.IsPaymentAllowed;
                    this.State.IsHasAdditionalDocuments = false;
                    this.NotifyReloadTitle();
                });
            });
        } else {
            this.Do(this.pdfEndpoint.GetPDFByLoginRequestID(this.State.EntryID), false, (response: BaseEntityResponse<PdfView>) => {
                this.State.PDFData = Utils.ConvertToBlob(response.Entity.Pdf, 'application/pdf');
                this.State.TotalSum = response.Entity.TotalSum;
                this.State.FileName = `${response.Entity.DocumentType} - ${response.Entity.DocumentNumber}`;
                this.State.IsPaymentAllowed = response.Entity.IsPaymentAllowed;
                this.State.IsHasAdditionalDocuments = response.Entity.IsHasAdditionalDocuments;
                this.NotifyReloadTitle();
            });
        }
    }
}
